"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { Select, SelectItem } from "@/components/ui/simple-select";
import type {
  StaffLineItem,
  HardCostLineItem,
  OfferingRoleId,
  HardCostCategoryId,
} from "@/lib/types/database/offerings";
import { OFFERING_ROLES, HARD_COST_CATEGORIES } from "@/lib/types/database/offerings";

interface StaffLineItemRowProps {
  item: StaffLineItem;
  onUpdate: (u: Partial<StaffLineItem>) => void;
  onRemove: () => void;
}

export function StaffLineItemRow({ item, onUpdate, onRemove }: StaffLineItemRowProps) {
  const roleDef = OFFERING_ROLES.find((r) => r.id === item.role);
  const displayRate = item.rate ?? roleDef?.defaultRate ?? 0;

  return (
    <div className="flex items-center gap-2 p-2 bg-card border rounded-md">
      <Select
        value={item.role}
        onValueChange={(v) => onUpdate({ role: v as OfferingRoleId })}
        className="w-40"
      >
        {OFFERING_ROLES.map((r) => (
          <SelectItem key={r.id} value={r.id}>
            {r.label} (${r.defaultRate}/hr)
          </SelectItem>
        ))}
      </Select>
      <Input
        type="number"
        min={0}
        step={0.5}
        placeholder="Hours"
        value={item.hours || ""}
        onChange={(e) => onUpdate({ hours: Number(e.target.value) || 0 })}
        className="w-20"
      />
      <Input
        type="number"
        min={0}
        step={1}
        placeholder={`Rate (default ${roleDef?.defaultRate ?? 0})`}
        value={item.rate ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          onUpdate({ rate: v === "" ? undefined : Number(v) });
        }}
        className="w-24"
      />
      <span className="text-xs text-muted-foreground">
        = ${(displayRate * item.hours).toFixed(2)}
      </span>
      <Button variant="ghost" size="sm" onClick={onRemove} className="h-8 w-8 p-0 text-destructive" title="Delete">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface HardCostLineItemRowProps {
  item: HardCostLineItem;
  onUpdate: (u: Partial<HardCostLineItem>) => void;
  onRemove: () => void;
}

export function HardCostLineItemRow({ item, onUpdate, onRemove }: HardCostLineItemRowProps) {
  return (
    <div className="flex items-center gap-2 p-2 bg-card border rounded-md">
      <Select
        value={item.description}
        onValueChange={(v) => onUpdate({ description: v as HardCostCategoryId })}
        className="w-40"
      >
        {HARD_COST_CATEGORIES.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.label}
          </SelectItem>
        ))}
      </Select>
      <Input
        type="number"
        min={0}
        step={0.01}
        placeholder="Amount"
        value={item.amount ?? ""}
        onChange={(e) => onUpdate({ amount: Number(e.target.value) || 0 })}
        className="w-24"
      />
      <span className="text-xs text-muted-foreground">${(item.amount ?? 0).toFixed(2)}</span>
      <Button variant="ghost" size="sm" onClick={onRemove} className="h-8 w-8 p-0 text-destructive" title="Delete">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
